import { forwardRef, useLayoutEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import type { NotebookNote } from '../../shared/types/notebook'
import type { SlashCommand } from '../../shared/types/chat'
import type { MenuCoords } from './useTiptapSlashMenu'

type MenuPhase = 'command_picker' | 'note_search'

interface SlashCommandMenuProps {
  phase: MenuPhase
  items: Array<SlashCommand | NotebookNote>
  selectedIdx: number
  coords: MenuCoords | null
  listboxId: string
  optionId: (idx: number) => string
  onSelectCommand: (cmd: SlashCommand) => void
  onSelectNote: (note: NotebookNote) => void
}

const MENU_WIDTH = 280
const VIEWPORT_MARGIN = 8

const SlashCommandMenu = forwardRef<HTMLDivElement, SlashCommandMenuProps>(function SlashCommandMenu(
  { phase, items, selectedIdx, coords, listboxId, optionId, onSelectCommand, onSelectNote },
  ref,
) {
  const { t } = useTranslation('notebook')
  const isCmd = phase === 'command_picker'
  const innerRef = useRef<HTMLDivElement | null>(null)

  // Measure menu after render and position it imperatively (avoids setState in
  // an effect). Hidden until positioned to prevent a flash at (0,0).
  useLayoutEffect(() => {
    const el = innerRef.current
    if (!el) return
    if (!coords) {
      el.style.visibility = 'hidden'
      return
    }
    const height = el.offsetHeight || 200
    const vh = window.innerHeight
    const vw = window.innerWidth

    let top = coords.bottom + 4
    if (top + height > vh - VIEWPORT_MARGIN) {
      const above = coords.top - height - 4
      top = above >= VIEWPORT_MARGIN
        ? above
        : Math.max(VIEWPORT_MARGIN, vh - height - VIEWPORT_MARGIN)
    }

    let left = coords.left
    if (left + MENU_WIDTH > vw - VIEWPORT_MARGIN) {
      left = vw - MENU_WIDTH - VIEWPORT_MARGIN
    }
    if (left < VIEWPORT_MARGIN) left = VIEWPORT_MARGIN

    el.style.position = 'fixed'
    el.style.top = `${top}px`
    el.style.left = `${left}px`
    el.style.width = `${MENU_WIDTH}px`
    el.style.visibility = 'visible'
  })

  const headingLabel = isCmd ? t('slashMenu.commandsHeading') : t('slashMenu.linkHeading')

  const menu = (
    <div
      ref={(el) => {
        innerRef.current = el
        if (typeof ref === 'function') ref(el)
        else if (ref) ref.current = el
      }}
      role="listbox"
      id={listboxId}
      aria-label={headingLabel}
      onMouseDown={e => e.preventDefault()}
      style={{ visibility: 'hidden', position: 'fixed' }}
      className="max-h-64 overflow-y-auto bg-charcoal border border-gold/20 rounded-xl shadow-xl z-50"
    >
      <div className="px-2 py-1.5 text-xs text-pearl/50 uppercase tracking-wider" aria-hidden="true">
        {headingLabel}
      </div>
      {items.map((item, i) => {
        const selected = i === selectedIdx
        return (
          <div
            key={isCmd ? (item as SlashCommand).id : (item as NotebookNote).id}
            id={optionId(i)}
            role="option"
            aria-selected={selected}
            onMouseDown={e => {
              e.preventDefault()
              if (isCmd) onSelectCommand(item as SlashCommand)
              else onSelectNote(item as NotebookNote)
            }}
            className={`flex items-center gap-2 w-full px-3 py-2 text-sm text-left transition-colors cursor-pointer ${
              selected ? 'bg-gold/10 text-gold' : 'text-ivory hover:bg-white/5'
            }`}
          >
            {isCmd ? (
              <>
                <span className="text-base flex-shrink-0">
                  {(item as SlashCommand).icon}
                </span>
                <div className="flex-1 min-w-0">
                  <span className="block truncate">{(item as SlashCommand).label}</span>
                  {(item as SlashCommand).description && (
                    <span className="block text-xs text-pearl/50 truncate">
                      {(item as SlashCommand).description}
                    </span>
                  )}
                </div>
              </>
            ) : (
              <>
                <span className="text-base flex-shrink-0">
                  {(item as NotebookNote).icon}
                </span>
                <span className="truncate">{(item as NotebookNote).title}</span>
              </>
            )}
          </div>
        )
      })}
    </div>
  )

  return typeof document !== 'undefined' ? createPortal(menu, document.body) : menu
})

export default SlashCommandMenu
